<template>
  <v-card>
    <div class="transaction-confirm-card">
      <div class="modal-title">
        {{
          $t("dialogs.transactionConfirm.title", {
            action: $t(`common.${activeTradeView}`),
          })
        }}
      </div>

      <div class="token-box-group">
        <div class="token-box">
          <div class="token-box-value">
            <FractionFormatter
              :fractional-price="
                formatFractionalPrice(Decimal.from(tokenA ?? 0))
              "
            />
          </div>
          <div class="token-box-symbol token-name">
            {{ isBuying ? AETERNITY_SYMBOL : token?.symbol }}
          </div>
        </div>

        <div class="token-box">
          <div class="token-box-value">
            <FractionFormatter
              :fractional-price="
                formatFractionalPrice(Decimal.from(tokenB ?? 0))
              "
            />
          </div>
          <div class="token-box-symbol token-name">
            {{ isBuying ? token?.symbol : AETERNITY_SYMBOL }}
          </div>
        </div>

        <div class="middle-arrow">
          <div class="arrow-icon">
            <v-icon size="x-small">mdi-arrow-down</v-icon>
          </div>
        </div>
      </div>

      <div class="estimated-text">
        {{
          $t("dialogs.transactionConfirm.estimatedOutput", {
            amount: formatFractionalPrice(Decimal.from(tokenB ?? 0)).number,
            symbol: isBuying ? token?.symbol : AETERNITY_SYMBOL,
          })
        }}
      </div>

      <div class="order-details-title">
        {{ $t("common.orderDetails") }}
      </div>

      <div class="details-list">
        <TransactionConfirmDetailRow
          v-if="!averageTokenPrice.isZero && !averageTokenPrice.infinite"
          :label="$t('common.avgTokenPrice')"
        >
          <LivePriceFormatter
            row
            :ae-price="averageTokenPrice"
            :watch-price="false"
          />
        </TransactionConfirmDetailRow>

        <TransactionConfirmDetailRow :label="$t('common.allowedSlippage')">
          {{ Number(desiredSlippage ?? 0).toFixed(2) }}%
        </TransactionConfirmDetailRow>

        <TransactionConfirmDetailRow :label="$t('common.priceImpact')">
          <div
            :class="[
              isBuying ? 'text-green' : 'text-red',
              'd-flex align-center ga-1',
            ]"
          >
            <div class="d-flex align-center">
              {{ isBuying ? "+" : "-"
              }}<FractionFormatter
                :fractional-price="formatFractionalPrice(priceImpactDiff)"
              />&nbsp;{{ AETERNITY_SYMBOL }}
            </div>
            <ImpactBadge
              :is-positive="isBuying"
              :is-zero="priceImpactDiff.isZero"
              :percentage="
                estimatedNextTokenPriceImpactDifferenceFormattedPercentage
              "
            />
          </div>
        </TransactionConfirmDetailRow>

        <TransactionConfirmDetailRow
          :label="$t('common.protocolTokenReward')"
          v-if="isBuying"
        >
          {{ protocolTokenReward }}
        </TransactionConfirmDetailRow>
      </div>

      <div class="buttons-row">
        <v-btn
          v-if="isSelling"
          block
          color="primary"
          size="large"
          :disabled="!isAllowSelling"
          @click="modalData.confirm && modalData.confirm()"
        >
          <span
            v-text="
              isAllowSelling
                ? $t('common.allowUseOfToken')
                : $t('common.useOfTokenAllowed')
            "
          />
        </v-btn>
        <v-btn
          block
          color="primary"
          size="large"
          :disabled="isSelling && isAllowSelling"
          @click="modalData.confirm && modalData.confirm()"
        >
          <span v-text="$t('common.confirm')" />
        </v-btn>
        <v-btn
          block
          variant="text"
          size="small"
          color="rgba(255, 255, 255, 0.5)"
          class="text-uppercase"
          @click="modalData.reject && modalData.reject()"
        >
          <span v-text="$t('common.cancel')" />
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useTransactionConfirmModal } from "@/stores/transactionConfirmModal";
import TransactionConfirmDetailRow from "@/components/Dialogs/TransactionConfirmDetailRow.vue";
import FractionFormatter from "@/components/Common/Pricing/FractionFormatter.vue";
import {
  AETERNITY_SYMBOL,
  PROTOCOL_DAO_AFFILIATION_FEE,
  PROTOCOL_DAO_TOKEN_AE_RATIO,
} from "@/utils/constants";
import { formatFractionalPrice } from "@/utils/common";
import { computed } from "vue";
import { Decimal } from "@/libs/decimal";
import { useTokenTradeStore } from "@/stores/tokenTradeStore";
import LivePriceFormatter from "@/components/Common/Pricing/LivePriceFormatter.vue";
import ImpactBadge from "@/components/Common/ImpactBadge.vue";

const {
  tokenA,
  tokenB,
  token,
  activeTradeView,
  isSelling,
  isBuying,
  isAllowSelling,
  priceImpactDiff,
  estimatedNextTokenPriceImpactDifferenceFormattedPercentage,
  desiredSlippage,
  averageTokenPrice,
  isUsingToken,
} = storeToRefs(useTokenTradeStore());

const { modalData } = storeToRefs(useTransactionConfirmModal());

const protocolTokenReward = computed(() => {
  const aeValue = isUsingToken.value ? tokenB.value : tokenA.value;
  return (
    Math.round(
      (1 - PROTOCOL_DAO_AFFILIATION_FEE) *
        PROTOCOL_DAO_TOKEN_AE_RATIO *
        (aeValue || 0) *
        100
    ) / 100
  );
});
</script>

<style lang="scss" scoped>
.transaction-confirm-card {
  position: relative;
  max-width: 460px;
  background: #1a1a1a;
  border: 1px solid #232323;
  border-radius: 10px;
  padding: 17px 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 15px;
  isolation: isolate;

  > .v-card-text,
  > .v-card-actions {
    padding: 0 !important;
  }
}

.modal-title {
  width: 100%;
  font-weight: 600;
  font-size: 18px;
  line-height: 13px;
  letter-spacing: 0.04em;
  color: #ffffff;
}

.highlight-token {
  margin-left: 5px;
  font-weight: 600;
  color: #ffffff;
}

.token-box-group {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.token-box {
  position: relative;
  width: 100%;
  height: 52px;
  border: 1px solid #373737;
  border-radius: 4px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #1a1a1a;
}

.token-box-value {
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0.03em;
  color: #ffffff;
}

.token-box-symbol {
  font-weight: 700;
  font-size: 14px;
  line-height: 10px;
  letter-spacing: -0.12em;
  text-transform: uppercase;
  color: #ffffff;
}

.middle-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  border: 1px solid #373737;
  background: #1a1a1a;
  width: 24px;
  height: 24px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  .arrow-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    background: #2d2d2d;
    border: 1px solid #373737;
  }
}

.estimated-text {
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.04em;
  color: #ffffff;
}

.order-details-title {
  font-weight: 600;
  font-size: 14px;
  line-height: 13px;
  color: #ffffff;
}

.details-list {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.buttons-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}
</style>
